import mapValues from 'lodash-es/mapValues'

import {
  searchFrontendLowerCaseParams,
  searchFrontendParamsSeoTransform
} from '../../constants/searchQueryWhitelist'
import { sanitizeQueryParams } from './sanitize'
import { ILocationParams, IQuery } from './types'

/**
 * Formatting Sort Params:
 * In the url we only store the sort filter as `?sort=trending'
 * but in public-search api it matches to `sort_by` and `sort_direction`
 * so whenever we match `sort` from the url, we have to parse it to
 * `sort_by` and `sort_direction` so we can fetch the correct results.
 * Sort by `price` is a special case where we append sort direction.
 * We also omit sort param if it is the default for a given site.
 * @param params
 * @param currentSite
 */
export const formatSortParam = (params: ILocationParams) => {
  const queryParams = { ...params }

  if (queryParams.sort_by) {
    if (queryParams.sort_by === 'price' && queryParams.sort_direction) {
      queryParams.sort = `price-${queryParams.sort_direction}`
      delete queryParams.sort_direction
    } else {
      queryParams.sort = queryParams.sort_by
    }
    delete queryParams.sort_by
  }

  return queryParams
}

const unformatSortParam = (params: ILocationParams) => {
  const searchLocationParams = { ...params }

  if (searchLocationParams.sort) {
    if (searchLocationParams.sort.includes('price-')) {
      searchLocationParams.sort_by = params.sort.split('-')[0]
      searchLocationParams.sort_direction = params.sort.split('-')[1]
    } else {
      searchLocationParams.sort_by = params.sort
    }
    delete searchLocationParams.sort
  }

  return searchLocationParams
}

/**
 * Converts {test: ['a', 'b', 'c']} to {test: 'a,b,c'}
 */
export const dearrayParamValues = (query: IQuery): ILocationParams => {
  return Object.keys(query).reduce((prev: ILocationParams, curr: string) => {
    const param = query[curr]
    prev[curr] = Array.isArray(param) ? param.join(',') : param
    return prev
  }, {})
}

export const decodeDashes = (value: string) => value.replace(/%2D/g, '-')

export const decodeLocationParams = (
  locationParams: ILocationParams
): ILocationParams =>
  mapValues(locationParams, value => {
    try {
      return decodeURIComponent(value)
    } catch (error) {
      return value
    }
  })

export const formatSearchLocationParams = (params: IQuery): ILocationParams => {
  return decodeLocationParams(
    mapValues(
      unformatSortParam(sanitizeQueryParams(dearrayParamValues(params))),
      (value, key) => {
        let nextValue = value

        if (key === 'term') {
          nextValue = nextValue.replace(/\+/g, ' ')
        }

        if (key === 'tags') {
          nextValue = nextValue.replace(/\+/g, ',')
        }

        if (searchFrontendLowerCaseParams.includes(key)) {
          nextValue = nextValue.toLowerCase()
        }

        if (searchFrontendParamsSeoTransform.includes(key)) {
          nextValue = nextValue.replace(/_/g, '-')
        }

        return nextValue
      }
    )
  )
}

export const encodeLocationParam = (value: string) => {
  return encodeURIComponent(value).replace(/-/g, '%2D')
}
